import { DatePicker, Input, Pagination, Select } from 'antd';
import React, { useMemo, useState } from 'react';
import { CheckCircleFilled, ClockCircleOutlined, SearchOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import es from 'antd/lib/date-picker/locale/es_ES';
import EstadoSolicitud, { estadoSolicitud } from '../Atoms/EstadoSolicitud';
import currencyFormatter from '../../Utils/currencyFormatter';
import decodeToken from '../../Utils/decodeToken';
import { isWorkflowRole } from '../../Utils/workflowRole';
import localeObjectTable from '../../Utils/localeObjectTable';
import Esqueleto from '../Atoms/Esqueleto';

// Agrupa los estados de EstadoSolicitud en las etapas del riel lateral.
const ETAPAS = {
  'Pendiente de jefe directo': 'por-aprobar',
  'Por aprobar (administrador)': 'por-aprobar',
  'En proceso': 'en-curso',
  'En adquisición': 'en-curso',
  Entregado: 'en-curso',
  Recibido: 'recibidas',
  Rechazada: 'sin-exito',
  Cancelada: 'sin-exito',
  Devolución: 'sin-exito'
};

const OPCIONES_ETAPA = [
  { value: 'todas', label: 'Todas' },
  { value: 'por-aprobar', label: 'Por aprobar' },
  { value: 'en-curso', label: 'En curso' },
  { value: 'recibidas', label: 'Recibidas' },
  { value: 'sin-exito', label: 'Rechazadas y otras' }
];

const fecha = (valor) => moment(valor, 'DD-MM-YYYY');

// Cada orden compara dos filas ya normalizadas; `reciente` es el orden en que llega la API.
const ORDENES = {
  reciente: null,
  antigua: (a, b) => fecha(a.created_at) - fecha(b.created_at),
  'total-mayor': (a, b) => b.total - a.total,
  'total-menor': (a, b) => a.total - b.total,
  solicitante: (a, b) => (a.user_name || '').localeCompare(b.user_name || ''),
  concepto: (a, b) => (a.spent_name || '').localeCompare(b.spent_name || '')
};

const Dato = ({ etiqueta, children }) => (
  <div className="sol-dato">
    <div className="sol-etiqueta">{etiqueta}</div>
    <div className="sol-valor">{children}</div>
  </div>
);
Dato.propTypes = { etiqueta: PropTypes.string, children: PropTypes.node };

// "PENDIENTE" llega del API cuando aún nadie autoriza.
const PasoAutorizacion = ({ rol, nombre }) => {
  const pendiente = nombre === 'PENDIENTE';
  return (
    <li className={pendiente ? 'sol-paso sol-paso--pendiente' : 'sol-paso sol-paso--hecho'}>
      {pendiente ? (
        <ClockCircleOutlined aria-hidden="true" />
      ) : (
        <CheckCircleFilled aria-hidden="true" />
      )}
      <span>
        <span className="sol-paso__rol">{rol}</span>
        <span className="sol-paso__nombre">{pendiente ? 'Pendiente' : nombre}</span>
      </span>
    </li>
  );
};
PasoAutorizacion.propTypes = { rol: PropTypes.string, nombre: PropTypes.string };

// Un esqueleto dentro de una caja con la altura de línea del texto que sustituye.
const Linea = ({ caja, ancho, alto = 12, className = '' }) => (
  <div className={`esqueleto-linea ${className}`} style={{ height: caja }}>
    <Esqueleto ancho={ancho} alto={alto} />
  </div>
);
Linea.propTypes = {
  className: PropTypes.string,
  caja: PropTypes.number,
  ancho: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  alto: PropTypes.number
};

// Misma estructura y medidas que una ficha de solicitud, para que al llegar los datos nada se mueva.
const FichaEsqueleto = () => (
  <div className="sol-item sol-item--esqueleto" aria-hidden="true">
    <div className="sol-item__principal">
      <div className="sol-item__cabecera" style={{ minHeight: 25 }}>
        <Esqueleto ancho={90} alto={20} />
        <Esqueleto ancho={110} alto={22} radio={11} />
      </div>
      <div className="sol-item__datos">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="sol-dato">
            <Linea caja={17} ancho={70} />
            <Linea caja={21} ancho="85%" alto={16} />
          </div>
        ))}
      </div>
    </div>
    <div className="sol-item__lado">
      <div className="sol-dato">
        <Linea caja={17} ancho={50} className="sol-etiqueta" />
        <Linea caja={25} ancho={100} alto={18} />
      </div>
      <Linea caja={17} ancho={80} className="sol-etiqueta" />
      <ul className="sol-pasos">
        {[0, 1].map((i) => (
          <li key={i} className="sol-paso" style={{ height: 38 }}>
            <Esqueleto ancho={16} alto={16} radio="50%" />
            <span style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              <Esqueleto ancho="50%" alto={11} />
              <Esqueleto ancho="75%" alto={13} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const TablaSolicitudesCompra = ({ data, loading }) => {
  const { RangePicker } = DatePicker;
  const navigate = useNavigate();
  const [etapa, setEtapa] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState();
  const [rango, setRango] = useState(null);
  const [orden, setOrden] = useState('reciente');
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(20);

  const decodedToken = decodeToken(localStorage.getItem('token'));
  const rol = decodedToken?.role?.name;
  const isIntegral = isWorkflowRole(rol);
  const isAdmin = rol === 'administrator' || isIntegral;
  const isSuperAdmin = rol === 'Super_Admin';
  const isShopping = rol === 'caja_chica' || isIntegral;
  const isAdquision = rol === 'Adquisiciones' || isIntegral;
  const veSolicitante = isAdmin || isSuperAdmin || isShopping || isAdquision;

  const filas = useMemo(
    () =>
      (data || []).map((item) => ({
        id: item?.id,
        created_at: item?.created_at,
        center_name: item?.center?.[0]?.center_name,
        spent_name: item?.spent?.[0]?.spent_name,
        approved_by: item?.approved_by ? item?.approved_by : 'PENDIENTE',
        admin_approved: item?.admin_approved ? item?.admin_approved : 'PENDIENTE',
        total: item?.total,
        user_name: item?.user_name,
        estado: estadoSolicitud(item?.purchase_table_name, item?.type_status, item?.approved_status)
          .label,
        table_name: item?.purchase_table_name,
        type_status: item?.type_status,
        approved_status: item?.approved_status
      })),
    [data]
  );

  const conteo = { todas: filas.length };
  filas.forEach((fila) => {
    const grupo = ETAPAS[fila.estado];
    conteo[grupo] = (conteo[grupo] || 0) + 1;
  });

  const filasVisibles = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    const lista = filas.filter((fila) => {
      if (etapa !== 'todas' && ETAPAS[fila.estado] !== etapa) return false;
      if (estado && fila.estado !== estado) return false;
      if (rango) {
        const creada = fecha(fila.created_at);
        if (creada.isBefore(rango[0].startOf('day')) || creada.isAfter(rango[1].endOf('day'))) {
          return false;
        }
      }
      if (!texto) return true;
      const campos = [`gto-${fila.id}`, fila.spent_name, fila.center_name];
      if (veSolicitante) campos.push(fila.user_name);
      return campos.some((campo) => (campo || '').toString().toLowerCase().includes(texto));
    });
    return ORDENES[orden] ? [...lista].sort(ORDENES[orden]) : lista;
  }, [filas, etapa, estado, rango, busqueda, orden, veSolicitante]);

  const cambiar = (setter) => (valor) => {
    setter(valor);
    setPagina(1);
  };

  const opcionesOrden = [
    { value: 'reciente', label: 'Más recientes' },
    { value: 'antigua', label: 'Más antiguas' },
    { value: 'total-mayor', label: 'Total: mayor a menor' },
    { value: 'total-menor', label: 'Total: menor a mayor' },
    ...(veSolicitante ? [{ value: 'solicitante', label: 'Solicitante (A-Z)' }] : []),
    { value: 'concepto', label: 'Concepto (A-Z)' }
  ];

  const inicio = (pagina - 1) * porPagina;
  const paginaActual = filasVisibles.slice(inicio, inicio + porPagina);

  const abrir = (id) => navigate(`/detalle-gasto/${id}`);

  return (
    <div className="solicitudes-bandeja mt-4">
      <nav className="solicitudes-riel" aria-label="Etapas de las solicitudes">
        {OPCIONES_ETAPA.map((opcion) => (
          <button
            key={opcion.value}
            type="button"
            aria-pressed={etapa === opcion.value}
            className={
              etapa === opcion.value ? 'solicitudes-riel__item is-activo' : 'solicitudes-riel__item'
            }
            onClick={() => cambiar(setEtapa)(opcion.value)}>
            <span>{opcion.label}</span>
            {loading ? (
              <Esqueleto ancho={22} alto={14} />
            ) : (
              <span className="solicitudes-riel__conteo">{conteo[opcion.value] || 0}</span>
            )}
          </button>
        ))}
      </nav>

      <div className="solicitudes-contenido">
        <div className="solicitudes-herramientas">
          <Input
            allowClear
            className="solicitudes-buscar"
            prefix={<SearchOutlined aria-hidden="true" />}
            placeholder={
              veSolicitante
                ? 'Buscar por solicitud, concepto, solicitante o centro'
                : 'Buscar por solicitud, concepto o centro'
            }
            aria-label="Buscar solicitudes"
            value={busqueda}
            onChange={(e) => cambiar(setBusqueda)(e.target.value)}
          />
          <Select
            allowClear
            className="solicitudes-filtro"
            placeholder="Estado"
            aria-label="Filtrar por estado"
            value={estado}
            onChange={cambiar(setEstado)}
            options={Object.keys(ETAPAS).map((e) => ({ value: e, label: e }))}
          />
          <RangePicker
            locale={es}
            className="solicitudes-filtro"
            placeholder={['Creada desde', 'Creada hasta']}
            value={rango}
            onChange={cambiar(setRango)}
          />
          <Select
            className="solicitudes-filtro"
            aria-label="Ordenar solicitudes"
            value={orden}
            onChange={cambiar(setOrden)}
            options={opcionesOrden}
          />
        </div>

        <div className="solicitudes-lista" aria-busy={!!loading}>
          {loading ? (
            <>
              <span className="sr-only" role="status">
                Cargando solicitudes
              </span>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <FichaEsqueleto key={i} />
              ))}
            </>
          ) : paginaActual.length === 0 ? (
            <div className="solicitudes-vacio">{localeObjectTable.emptyText}</div>
          ) : (
            paginaActual.map((fila) => (
              <article
                key={fila.id}
                className="sol-item"
                onClick={(evento) => {
                  // Los enlaces y controles de la fila conservan su propio comportamiento.
                  if (evento.target.closest('a, button, input')) return;
                  abrir(fila.id);
                }}>
                <div className="sol-item__principal">
                  <div className="sol-item__cabecera">
                    <Link to={`/detalle-gasto/${fila.id}`} className="solicitudes-id">
                      GTO-{fila.id}
                    </Link>
                    <EstadoSolicitud
                      tableName={fila.table_name}
                      typeStatus={fila.type_status}
                      approvedStatus={fila.approved_status}
                    />
                  </div>
                  <div className="sol-item__datos">
                    <Dato etiqueta="Concepto">
                      <strong>{fila.spent_name}</strong>
                    </Dato>
                    {veSolicitante ? <Dato etiqueta="Solicitante">{fila.user_name}</Dato> : null}
                    <Dato etiqueta="Centro de costos">{fila.center_name}</Dato>
                    <Dato etiqueta="Creada">{fila.created_at}</Dato>
                  </div>
                </div>
                <div className="sol-item__lado">
                  <Dato etiqueta="Total">
                    <span className="solicitudes-total sol-total">
                      {currencyFormatter(fila.total)}
                    </span>
                  </Dato>
                  <div className="sol-etiqueta">Autorización</div>
                  <ul className="sol-pasos">
                    <PasoAutorizacion rol="Jefe directo" nombre={fila.approved_by} />
                    <PasoAutorizacion rol="Administrador" nombre={fila.admin_approved} />
                  </ul>
                </div>
              </article>
            ))
          )}
        </div>

        {loading ? null : (
          <Pagination
            className="solicitudes-paginacion mt-3 mb-4"
            current={pagina}
            pageSize={porPagina}
            total={filasVisibles.length}
            pageSizeOptions={[20, 40, 60, 80, 100]}
            showSizeChanger
            hideOnSinglePage={false}
            showTotal={(total, range) =>
              `Mostrando solicitudes del ${range[0]} al ${range[1]} de un total de ${total}`
            }
            onChange={(p, size) => {
              setPagina(p);
              setPorPagina(size);
            }}
          />
        )}
      </div>
    </div>
  );
};

TablaSolicitudesCompra.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool
};
export default TablaSolicitudesCompra;
